import {
  CultureTypeDistribution,
  CultureTypeDistributionJSON,
  StartSurveyPayload,
  StartSurveyPayloadJSON,
  Survey,
  SurveyJSON,
  SurveyResponse,
  SurveyResponseJSON,
  Surveys,
  SurveysJSON,
} from '../types/SurveyTypes';

const defaultSurveyConfiguration = {
  omitDepartment: false,
  omitSpokenLanguage: false,
  omitGender: false,
  omitLocation: false,
  omitAgeGroup: false,
  omitWorkMode: false,
  omitFeedback: false,
};

export default class SurveySerializer {
  private static toCultureTypeDistribution(payload?: CultureTypeDistributionJSON): CultureTypeDistribution | undefined {
    if (!payload) return undefined;

    return {
      management: (payload.management ?? []).map((item) => ({
        methodType: {
          resourceId: item.method_type.resource_id,
          identifier: item.method_type.identifier,
          imageUrl: item.method_type.image_url,
          name: item.method_type.name,
        },
        count: item.count,
      })),
      employees: (payload.employees ?? []).map((item) => ({
        methodType: {
          resourceId: item.method_type.resource_id,
          identifier: item.method_type.identifier,
          imageUrl: item.method_type.image_url,
          name: item.method_type.name,
        },
        count: item.count,
      })),
    };
  }

  static toObject(payload: SurveyJSON): Survey {
    const cultureTypeDistribution = payload.statistics.culture_type_distribution ?? payload.culture_type_distribution;

    return {
      resourceId: payload.resource_id,
      surveyCode: payload.survey_code,
      defaultLanguage: payload.default_language,
      startsAt: payload.starts_at,
      surveyType: payload.survey_type,
      createdAt: payload.created_at,
      updatedAt: payload.updated_at,
      endsAt: payload.ends_at,
      startedAt: payload.started_at,
      endedAt: payload.ended_at,
      reopenedAt: payload.reopened_at,
      reopenedBy: payload.reopened_by,
      closedBy: payload.closed_by,
      autoClose: payload.auto_close,
      recurrenceType: payload.recurrence_type,
      expectedParticipants: payload.expected_participants,
      cultureAnalysis: payload.culture_analysis
        ? {
          status: payload.culture_analysis.status,
        }
        : null,
      configuration: {
        omitDepartment: payload.configuration?.omit_department ?? defaultSurveyConfiguration.omitDepartment,
        omitSpokenLanguage:
          payload.configuration?.omit_spoken_language ?? defaultSurveyConfiguration.omitSpokenLanguage,
        omitGender: payload.configuration?.omit_gender ?? defaultSurveyConfiguration.omitGender,
        omitLocation: payload.configuration?.omit_location ?? defaultSurveyConfiguration.omitLocation,
        omitAgeGroup: payload.configuration?.omit_age_group ?? defaultSurveyConfiguration.omitAgeGroup,
        omitWorkMode: payload.configuration?.omit_work_mode ?? defaultSurveyConfiguration.omitWorkMode,
        omitFeedback: payload.configuration?.omit_feedback ?? defaultSurveyConfiguration.omitFeedback,
      },
      cultureTypeDistribution: this.toCultureTypeDistribution(cultureTypeDistribution),
      statistics: {
        resourceId: payload.statistics.resource_id,
        companySurveyId: payload.statistics.company_survey_id,
        totalRespondents: payload.statistics.total_respondents,
        managerRespondents: payload.statistics.manager_respondents,
        employeeRespondents: payload.statistics.employee_respondents,
        averageAgeGroup: payload.statistics.average_age_group,
        respondentsPerGender: {
          masculine: payload.statistics.respondents_per_gender.masculine,
          feminine: payload.statistics.respondents_per_gender.feminine,
          diverse: payload.statistics.respondents_per_gender.diverse,
          preferNotToSay: payload.statistics.respondents_per_gender.prefer_not_to_say,
        },
        respondentsPerLocation: payload.statistics.respondents_per_location?.map((location) => ({
          location: location.location,
          respondents: location.respondents,
        })),
        respondentsPerLanguage: {
          de: payload.statistics.respondents_per_language.de,
          en: payload.statistics.respondents_per_language.en,
          both: payload.statistics.respondents_per_language.both,
        },
        respondentsPerDepartment: payload.statistics.respondents_per_department?.map((respondersPerDepartment) => ({
          department: {
            resourceId: respondersPerDepartment?.department?.resource_id,
            name: {
              en: respondersPerDepartment?.department?.name.en,
              de: respondersPerDepartment?.department?.name.de,
            },
          },
          respondents: respondersPerDepartment.respondents,
        })),
        cultureTypeDistribution: this.toCultureTypeDistribution(cultureTypeDistribution),
      },
    };
  }

  static toObjects(payload: SurveysJSON): Surveys {
    return {
      cultureSurveys: payload.culture_surveys.map((survey) => this.toObject(survey)),
      happinessSurveys: payload.happiness_surveys.map((survey) => this.toObject(survey)),
    };
  }

  static toJSONPayloadCreate(payload: StartSurveyPayload): StartSurveyPayloadJSON {
    return {
      company_survey: {
        survey_type: payload.companySurvey.surveyType,
        starts_at: payload.companySurvey.startsAt,
        ends_at: payload.companySurvey.endsAt,
        auto_close: payload.companySurvey.autoClose,
        default_language: payload.companySurvey.defaultLanguage,
        expected_participants: payload.companySurvey.expectedParticipants,
        omit_department: payload.companySurvey.omitDepartment,
        omit_spoken_language: payload.companySurvey.omitSpokenLanguage,
        omit_gender: payload.companySurvey.omitGender,
        omit_location: payload.companySurvey.omitLocation,
        omit_age_group: payload.companySurvey.omitAgeGroup,
        omit_work_mode: payload.companySurvey.omitWorkMode,
        omit_feedback: payload.companySurvey.omitFeedback,
      },
    };
  }

  static toObjectResponseCreate(payload: SurveyResponseJSON): SurveyResponse {
    return {
      companySurvey: this.toObject(payload.company_survey),
    };
  }
}
